const IMAGE_MAX_BYTES = 100 * 1024;
const MAX_DIMENSION = 1280;

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

async function compressCanvas(canvas, maxBytes) {
  let quality = 0.85;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);

  while (blob && blob.size > maxBytes && quality > 0.3) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }

  if (blob && blob.size > maxBytes) {
    const scale = 0.85;
    let w = canvas.width;
    let h = canvas.height;
    const tmp = document.createElement('canvas');
    const ctx = tmp.getContext('2d');

    while (blob && blob.size > maxBytes && w > 200 && h > 200) {
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      tmp.width = w;
      tmp.height = h;
      ctx.drawImage(canvas, 0, 0, w, h);
      blob = await canvasToBlob(tmp, 'image/jpeg', Math.max(0.3, quality));
    }
  }

  if (!blob || blob.size > maxBytes) {
    throw new Error('Image could not be compressed below 100KB');
  }

  return blob;
}

export function isImageUploadFile(file) {
  if (!file) return false;
  if (file.type?.startsWith('image/')) return true;
  const ext = file.name?.split('.').pop()?.toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext || '');
}

/**
 * Compress image file to ≤100KB JPEG for upload.
 */
export async function compressImageForUpload(file) {
  const img = await loadImageFromFile(file);
  let { width, height } = img;

  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await compressCanvas(canvas, IMAGE_MAX_BYTES);
  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

/**
 * Compress if image; otherwise return file unchanged.
 */
export async function prepareFileForUpload(file) {
  if (!file || !isImageUploadFile(file)) {
    return file;
  }
  return compressImageForUpload(file);
}

export const UPLOAD_IMAGE_MAX_BYTES = IMAGE_MAX_BYTES;

export function formatUploadFileSize(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
